import { conformsTo, isString } from "lodash";
import { useEffect, useState } from "react";

import { AvailableIntegrationFragment } from "@/apollo/types";

import { useIntegration } from "../IntegrationProvider";
import { WEBSITE_DOCS_API_URL } from "@/constants";

export const useIntegrationDocumentationResource = (
  integration?: Pick<
    AvailableIntegrationFragment,
    "id" | "aliasMetadata"
  > | null,
  type?: "destination",
) => {
  const [data, setData] = useState<{
    integrationId: string;
    href: string;
    embedHref: string;
    title: string;
    setupSectionIds?: {
      etl?: string;
      reverseEtl?: string;
    };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchDocumentationInfo = async (integrationId: string) => {
      setLoading(true);
      try {
        const normalizedIntegrationId = integrationId
          .toLowerCase()
          .replace(/-v\d+$/, ""); // Remove version suffix, i.e. forex-v2 => forex
        const response = await fetch(
          `${WEBSITE_DOCS_API_URL}/${normalizedIntegrationId}${type ? `?type=${type}` : ""}`,
        );
        if (!response.ok) {
          return null;
        }
        const data = await response.json();
        if (
          !conformsTo(data, {
            integrationId: isString,
            href: isString,
            embedHref: isString,
            title: isString,
          })
        ) {
          // eslint-disable-next-line no-console
          console.error("Invalid response from docs API");
          return null;
        }
        return data as {
          integrationId: string;
          href: string;
          embedHref: string;
          title: string;
        };
      } catch (_error) {
        return null;
      } finally {
        setLoading(false);
      }
    };

    async function resolveDocumentationResource(
      integration: Pick<AvailableIntegrationFragment, "id" | "aliasMetadata">,
    ) {
      const primary = await fetchDocumentationInfo(integration.id);
      if (primary !== null) {
        return primary;
      }
      if (integration.aliasMetadata) {
        return await fetchDocumentationInfo(integration.aliasMetadata.id);
      }
      return null;
    }

    async function loadDocumentationResource(
      integration: Pick<AvailableIntegrationFragment, "id" | "aliasMetadata">,
    ) {
      const resource = await resolveDocumentationResource(integration);
      if (resource) {
        setData(resource);
      }
    }

    setData(null);
    if (integration) {
      loadDocumentationResource(integration);
    }
  }, [integration, type]);

  return {
    data,
    loading,
  };
};

export const useIntegrationDocumentationUrl = (
  integrationId: string | undefined,
  isDatawarehouse: boolean,
) => {
  const integration = useIntegration(integrationId);

  const { data } = useIntegrationDocumentationResource(
    integration,
    isDatawarehouse ? "destination" : undefined,
  );

  return data?.href ?? null;
};
